// eslint-disable-next-line import/no-cycle
import requests from '@client/store/modules/account/requests';
import Account from '@client/models/store/account';
import Portfolio from '@client/models/portfolio';
// eslint-disable-next-line import/no-cycle
import accountService from '@client/services/account';
// eslint-disable-next-line import/no-cycle
import investmentService from '@client/services/investment';
// eslint-disable-next-line import/no-cycle
import custodianService from '@client/services/custodian';
// eslint-disable-next-line import/no-cycle
import wealthProjectionService from '@client/services/wealthProjection';
import {Logger, Category} from '@simpleinvest/front-end-logger';
import accountTypeId from '@client/enums/accountTypeId';
import b64toBlob from 'b64-to-blob';

const logger = new Logger({
  category: Category.Goals,
  fileName: 'account.js',
});

const stateObj = () => new Account();

const getters = {
  summary: ({summary}) => summary,
  currentPortfolio: (state) => state.currentPortfolio,
  targetPortfolio: (state) => state.targetPortfolio,
  wealthProjection: (state) => state.wealthProjection,
  accountTypes: (state) => state.accountTypes,
  isIndividualAccount: (state) => state.toAdd && state.toAdd.account_type_id === accountTypeId.individual,
  showCatAccountHolderType: (_state, getterObj) => getterObj.isIndividualAccount,
  isCatAccountHolderTypeRequired: (_state, getterObj, _rootState, rootGetters) =>
    getterObj.showCatAccountHolderType && rootGetters['account/requests/isFaiAffiliationSet'],
  custodianLastLoadDate: (state) => state.custodianLastLoadDate,
  accountToAdd: (state) => state.toAdd,
  isTraditionalIraAccount: (state) => state.toAdd && state.toAdd.account_type_id === accountTypeId.traditionalIra,
  isRothIraAccount: (state) => state.toAdd && state.toAdd.account_type_id === accountTypeId.rothIra,
  selectedAccountType: (state) => state.toAdd?.account_name,
  accountAlreadyExist: (_state, getterObj, _rootState, rootGetters) => {
    const accountList = rootGetters['accountList/accountList'];
    const alreadyExistAccounts = accountList
      .filter((item) => item.isLive)
      .filter((item) => item.type === getterObj.selectedAccountType);

    return alreadyExistAccounts.length > 0;
  },
};

const actions = {
  async getAccountSummary({commit}, accountId) {
    const [summary] = await accountService.getAccountSummary({accountId});

    commit('SET_ACCOUNT_SUMMARY', summary);

    return summary;
  },
  async getPerformanceReport({rootGetters}, {accountIds, beginDate, endDate}) {
    const clUserId = rootGetters['cognitoUser/userId'];

    const {data} = await investmentService.getPerformanceReportByAccountList({
      clientId: clUserId,
      accountList: accountIds,
      beginDate,
      endDate,
    });

    return data.error ? [] : data;
  },
  async getLatestCustodianLoadData({commit, rootGetters}) {
    const custodianId = rootGetters['user/custodianId'];
    const result = await custodianService.latestCustodianLoadDate();

    const custodianLastLoadDate = result.find((item) => Number(item.custodianId) === Number(custodianId));

    commit('SET_CUSTODIAN_LAST_LOAD_DATE', custodianLastLoadDate);

    return custodianLastLoadDate;
  },
  async getWealthProjection({commit}, accountId) {
    const result = await wealthProjectionService.getWealthProjectionByAccountId(accountId);

    commit('SET_WEALTH_PROJECTION', result);

    return result;
  },
  async getTargetPortfolio({commit}, accountId) {
    const result = await accountService.getTargetPortfolio({accountId});

    commit('SET_TARGET_PORTFOLIO', result);
  },
  async getCurrentPortfolio({commit}, accountId) {
    try {
      const result = await accountService.getCurrentPortfolio({accountId});

      commit('SET_CURRENT_PORTFOLIO', result);
    } catch (error) {
      logger.addBreadcrumb({
        callingFuncName: 'getCurrentPortfolio',
        message: `Error getting current account portfolio : ${error.message}`,
      });
      logger.logException({error});
    }
  },
  batchId({commit}, payload) {
    commit('SET_BATCH_ID', payload);
  },
  accountTypeToAdd({commit}, payload) {
    commit('SET_ACCOUNT_TYPE_TO_ADD', payload);
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  downloadPdf({commit}, payload) {
    const {data, filename} = payload;
    const blob = b64toBlob(data, 'application/pdf');
    const targetURL = window.URL || window.webkitURL;
    const blobUrl = targetURL.createObjectURL(blob);
    const timestamp = Date.now();
    const link = document.createElement('a');

    document.body.append(link);
    link.href = blobUrl;
    link.target = '_blank';
    link.download = `${timestamp} - ${filename}`;
    link.style = 'display: none';
    link.click();
    targetURL.revokeObjectURL(blobUrl);
  },
  async getAccountTypeList({commit, rootGetters}) {
    const clUserId = rootGetters['cognitoUser/userId'];
    const data = await accountService.getAvailableAccountTypes({clUserId});

    commit('SET_ACCOUNT_TYPES', data);
  },
  async getCustodianInfo({commit}, payload) {
    const response = await accountService.getCustodianInfo({
      riaId: String(payload.riaId),
      custodianId: Number(payload.custodianId),
    });

    if (response?.length > 0) {
      commit('SET_CUSTODIAN_INFO', response[0]);

      return response[0];
    }

    return response;
  },
  async performanceSummary({rootGetters}, {accountIds, beginDate, endDate}) {
    const clUserId = rootGetters['cognitoUser/userId'];
    let resp;

    try {
      resp = await investmentService.postPerformanceSummary({
        clientId: clUserId,
        accountIds,
        beginDate,
        asOfDate: endDate,
      });
    } catch (error) {
      logger.addBreadcrumb({
        callingFuncName: 'performanceSummary',
        message: `Error getting performance data : ${error.message}`,
      });
      logger.logException({error});
      resp = [];
    }

    return resp;
  },
  async timeBasedCalendarCalculations(_, payload) {
    let resp;

    try {
      resp = await investmentService.postTimebasedCalendarCalculations({
        accountIds: payload.accountIds,
        asOfDate: payload.endDate,
      });
    } catch (error) {
      logger.addBreadcrumb({
        callingFuncName: 'timeBasedCalendarCalculations',
        message: `Error getting historical data : ${error.message}`,
      });
      logger.logException({error});
      resp = [];
    }

    return resp;
  },
  async timeBasedTrailingYearPerformance(_, payload) {
    let resp;

    try {
      resp = await investmentService.postTimebasedTrailingYearPerformance({
        accountIds: payload.accountIds,
        asOfDate: payload.endDate,
      });
    } catch (error) {
      logger.addBreadcrumb({
        callingFuncName: 'timeBasedTrailingYearPerformance',
        message: `Error getting historical data : ${error.message}`,
      });
      logger.logException({error});
      resp = [];
    }

    return resp;
  },
};

const mutations = {
  SET_ACCOUNT_SUMMARY(state, summary) {
    state.summary = summary;
  },
  SET_CUSTODIAN_LAST_LOAD_DATE(state, custodianLastLoadDate) {
    state.custodianLastLoadDate = custodianLastLoadDate;
  },
  SET_WEALTH_PROJECTION(state, payload) {
    state.wealthProjection = payload;
  },
  SET_CURRENT_PORTFOLIO(state, payload) {
    state.currentPortfolio = new Portfolio(payload[0]);
  },
  SET_TARGET_PORTFOLIO(state, payload) {
    state.targetPortfolio = new Portfolio(payload[0]);
  },
  SET_BATCH_ID(state, payload) {
    state.batchId = payload;
  },
  SET_ACCOUNT_TYPE_TO_ADD(state, payload) {
    state.toAdd = payload;
  },

  SET_ACCOUNT_TYPES(state, payload) {
    state.accountTypes = payload;
  },
  SET_CUSTODIAN_INFO(state, payload) {
    state.custodianInfo = payload;
  },
};

export default {
  namespaced: true,
  state: stateObj,
  getters,
  actions,
  mutations,
  modules: {
    requests,
  },
};
